/**
 * GET api/businesses/admin/transactions?type=deposits|payouts
 *
 * The two types return structurally different rows — a deposit carries
 * `currency`/`network`/`txid`/`paymentPage`, a payout carries
 * `asset`/`payoutAmount`/`recipient` — so rows stay loosely typed and the
 * table/detail layers narrow on `transactionType`.
 */

export type BusinessTransactionType = "deposits" | "payouts";

export type BusinessTransactionStatusFilter =
  | "all"
  | "COMPLETED"
  | "PENDING"
  | "FAILED";

export const BUSINESS_TRANSACTION_TYPES: {
  value: BusinessTransactionType;
  label: string;
}[] = [
  { value: "deposits", label: "Deposits" },
  { value: "payouts", label: "Payouts" },
];

export const BUSINESS_TRANSACTION_STATUSES: {
  value: BusinessTransactionStatusFilter;
  label: string;
}[] = [
  { value: "all", label: "All statuses" },
  { value: "COMPLETED", label: "Completed" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

/** Nested business snapshot embedded on every transaction. */
export type BusinessTransactionBusiness = {
  _id?: string;
  name?: string;
  email?: string;
  ownerUserId?: string;
  type?: string;
  details?: {
    industry?: string;
    address?: string;
    country?: string;
  };
  isVerified?: boolean;
  status?: string;
  createdAt?: string;
};

export type BusinessTransactionOwner = {
  _id?: string;
  email?: { address?: string };
  profile?: {
    firstName?: string;
    lastName?: string;
    username?: string;
  };
};

export type BusinessTransactionPaymentPage = {
  _id?: string;
  name?: string;
  reference?: string;
  businessId?: string;
  amount?: number;
};

export type BusinessTransactionRecipient = {
  accountName?: string;
  accountNumber?: string;
  bankCode?: string;
  bankName?: string;
};

export type AdminBusinessTransaction = Record<string, unknown> & {
  _id: string;
  /** "deposit" | "payout" — singular, unlike the plural `type` query param. */
  transactionType?: string;
  status?: string;
  business?: BusinessTransactionBusiness | null;
  owner?: BusinessTransactionOwner | null;
  paymentPage?: BusinessTransactionPaymentPage | null;
  recipient?: BusinessTransactionRecipient | null;
};

export type AdminBusinessTransactionsMeta = {
  currentPage: number;
  limit: number;
  totalTransactions: number;
  totalPages: number;
};

export type AdminBusinessTransactionsFilters = {
  businessId: string | null;
  type: string | null;
  status: string | null;
  from: string | null;
  to: string | null;
};

export type AdminBusinessTransactionsData = {
  transactions: AdminBusinessTransaction[];
  meta: AdminBusinessTransactionsMeta;
  filters: AdminBusinessTransactionsFilters;
};

export type AdminBusinessTransactionsEnvelope = {
  status: string;
  message: string;
  data: AdminBusinessTransactionsData;
};
